<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import dayjs from 'dayjs'
import { useDevice } from '#imports'
import { useSports } from '~/composables/sports/use-sports'
import { DATE_TIME_FORMAT } from '~/constants'
import type { MatchData } from '~/utils/interface'

const BaseImage = defineAsyncComponent(async () => await import('~/components/common/base-image.vue'))

defineProps<{ hotmatch: MatchData }>()

const { isMobile } = useDevice()
const { openKSportGame, playASportGame } = useSports()

const dateTimeConverted = (time: string, format: string): string => {
  const date = dayjs.utc(time.trim(), DATE_TIME_FORMAT.FULL_DATE_FORMAT_SCHEDULE).local()
  return date.format(format)?.replace('Invalid Date', '--/--/--')
}
</script>

<template>
  <div :class="['match-card', { mobile: isMobile }]">
    <h6>{{ hotmatch?.league_name?.toUpperCase() }}</h6>
    <div class="match-teams">
      <div class="home-team">
        <p>{{ hotmatch?.teams[0].name }}</p>
        <div class="team-logo">
          <BaseImage
            :src="hotmatch?.teams[0].flag_thumbnail"
            :alt="hotmatch?.teams[0].name"
            imageDefault="/assets/images/common/no-item.png"
          />
        </div>
      </div>
      <div class="match-time">
        <p class="time">
          {{ dateTimeConverted(hotmatch?.text_time, DATE_TIME_FORMAT.HOUR_MINUTE_DEFAULT) }}
        </p>
        <p v-if="!isMobile" class="dash">-</p>
        <p class="date">{{ dateTimeConverted(hotmatch?.text_time, DATE_TIME_FORMAT.DAY_MONTH) }}</p>
      </div>
      <div class="away-team">
        <div class="team-logo">
          <BaseImage
            :src="hotmatch?.teams[1].flag_thumbnail"
            :alt="hotmatch?.teams[1].name"
            imageDefault="/assets/images/common/no-item.png"
          />
        </div>
        <p>{{ hotmatch?.teams[1].name }}</p>
      </div>
    </div>
    <div class="handicap">
      <p>Kèo Chấp</p>
      <p>Kèo Tài Xỉu</p>
    </div>
    <div class="odds">
      <div class="odds__handicap-block">
        <div class="blocks">
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.hTeam?.odds) > 0,
                down: Number(hotmatch?.hTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.hTeam?.odds || '-' }}
          </div>
          <div class="box">
            {{ hotmatch?.hTeam?.rate || '-' }}
          </div>
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.aTeam?.odds) > 0,
                down: Number(hotmatch?.aTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.aTeam?.odds || '-' }}
          </div>
        </div>
        <div class="blocks">
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.athena_ahFT?.hTeam?.odds) > 0,
                down: Number(hotmatch?.athena_ahFT?.hTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.athena_ahFT?.hTeam?.odds || '-' }}
          </div>
          <div class="box">
            {{ hotmatch?.athena_ahFT?.hTeam?.rate || '-' }}
          </div>
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.athena_ahFT?.aTeam?.odds) > 0,
                down: Number(hotmatch?.athena_ahFT?.aTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.athena_ahFT?.aTeam?.odds || '-' }}
          </div>
        </div>
      </div>
      <div class="odds__btns">
        <button class="k-sport" @click.prevent="openKSportGame(hotmatch)">K-SPORTS</button>
        <button class="a-sport" @click.prevent="playASportGame(hotmatch)">A-SPORTS</button>
      </div>
      <div class="odds__over-under-block">
        <div class="blocks">
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.overFT?.hTeam?.odds) > 0,
                down: Number(hotmatch?.overFT?.hTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.overFT?.hTeam?.odds || '-' }}
          </div>
          <div class="box">{{ hotmatch?.overFT?.hTeam?.rate || '-' }}</div>
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.overFT?.aTeam?.odds) > 0,
                down: Number(hotmatch?.overFT?.aTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.overFT?.aTeam?.odds || '-' }}
          </div>
        </div>
        <div class="blocks">
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.athena_overFT?.hTeam?.odds) > 0,
                down: Number(hotmatch?.athena_overFT?.hTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.athena_overFT?.hTeam?.odds || '-' }}
          </div>
          <div class="box">{{ hotmatch?.athena_overFT?.hTeam?.rate || '-' }}</div>
          <div
            :class="
              getClassNames({
                box: true,
                up: Number(hotmatch?.athena_overFT?.aTeam?.odds) > 0,
                down: Number(hotmatch?.athena_overFT?.aTeam?.odds) <= 0
              })
            "
          >
            {{ hotmatch?.athena_overFT?.aTeam?.odds || '-' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.match-card {
  border-radius: 6px;
  background: $white;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.04);
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  h6 {
    color: $primary-color;
    text-align: center;
    font-size: 14px;
    font-weight: $font-weight-bold;
    line-height: 24px;
    width: 100%;
  }
  .match-teams {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    margin: 8px 0;
    height: 32px;
    .home-team {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 8px;
      flex: 1 0 0;
      max-width: 136px;
      p {
        overflow: hidden;
        color: $neutral-800;
        text-align: right;
        text-overflow: ellipsis;
        font-size: 16px;
        white-space: nowrap;
        font-style: normal;
        font-weight: $font-weight-medium;
        line-height: 24px;
      }
      .team-logo {
        min-width: 32px;
        max-width: 32px;
      }
    }
    .away-team {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1 0 0;
      max-width: 136px;
      p {
        overflow: hidden;
        color: $neutral-800;
        text-overflow: ellipsis;
        font-size: 16px;
        white-space: nowrap;
        font-style: normal;
        font-weight: $font-weight-medium;
        line-height: 24px;
      }
      .team-logo {
        min-width: 32px;
        max-width: 32px;
      }
    }
    .match-time {
      display: flex;
      width: 87px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      align-self: stretch;
      .time,
      .date {
        color: $neutral-700;
        text-align: center;
        font-size: 14px;
        font-weight: $font-weight-medium;
        line-height: 24px;
      }
      .dash {
        color: $neutral-400;
        text-align: center;
        font-size: 16px;
        font-weight: $font-weight-normal;
        line-height: 24px;
      }
    }
  }
  .handicap {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    align-self: stretch;
    color: $neutral-600;
    font-size: 12px;
    font-weight: $font-weight-normal;
    line-height: 16px;
  }
  .odds {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 8px;
    width: 100%;
    &__handicap-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      flex: 1 0 0;
      .blocks {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
        .box {
          cursor: pointer;
          height: 24px;
          display: flex;
          align-items: center;
          gap: 8px;
          border-radius: 6px;
          background: $neutral-100;
          flex-direction: column;
          justify-content: center;
          flex: 1 0 0;
          align-self: stretch;
          color: $neutral-800;
          font-size: 14px;
          font-weight: $font-weight-normal;
          &.up {
            color: $green-500 !important;
            border-radius: 6px;
            border: 1px solid transparent;
            background: linear-gradient($neutral-100, $neutral-100) padding-box,
              linear-gradient(to right, #fff 35%, $green-500) border-box;
          }
          &.down {
            color: $primary-color !important;
            border-radius: 6px;
            border: 1px solid transparent;
            background: linear-gradient($neutral-100, $neutral-100) padding-box,
              linear-gradient(to right, #fff 35%, $primary-color) border-box;
          }
        }
      }
    }
    &__btns {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      line-height: 16px;
      .k-sport,
      .a-sport {
        display: flex;
        padding: 4px 8px;
        justify-content: center;
        align-items: center;
        border-radius: 4px;
        background: $primary-color;
        outline: none;
        border: none;
        color: $white;
      }
      .a-sport {
        background: $green-500;
      }
    }
    &__over-under-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      flex: 1 0 0;
      .blocks {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
        .box {
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          height: 24px;
          border-radius: 6px;
          background: $neutral-100;
          flex-direction: column;
          justify-content: center;
          flex: 1 0 0;
          align-self: stretch;
          color: $neutral-800;
          font-size: 14px;
          font-weight: $font-weight-normal;
          &.up {
            color: $green-500 !important;
            border-radius: 6px;
            border: 1px solid transparent;
            background: linear-gradient($neutral-100, $neutral-100) padding-box,
              linear-gradient(to right, #fff 35%, $green-500) border-box;
          }
          &.down {
            color: $primary-color !important;
            border-radius: 6px;
            border: 1px solid transparent;
            background: linear-gradient($neutral-100, $neutral-100) padding-box,
              linear-gradient(to right, #fff 35%, $primary-color) border-box;
          }
        }
      }
    }
  }

  &.mobile {
    padding: 8px;
    gap: 4px;
    .match-teams {
      gap: 12px;
      height: 48px;
      margin: 0;
      .home-team p,
      .away-team p {
        font-size: 14px;
      }
    }
    .match-time {
      flex-direction: column;
      gap: 0;
      width: unset;
    }
    .odds__handicap-block .blocks,
    .odds__over-under-block .blocks {
      gap: 4px;
    }
  }
}
</style>
